import { useLocation } from "@solidjs/router";
import { getIdPrefix, isPageSlug, type PageSlug } from "@storytell/core";
import type { StreamingBlockId, StreamingChatId } from "@storytell/streaming";
import type { AssetId, ConceptId, LabelId, ViewId } from "@storytell/types";
import { createMemo, Match, Show, Suspense, Switch } from "solid-js";
import { WorkspaceLoading } from "~/components/loading/WorkspaceLoading";
import { lazy } from "~/lib/lazyWithReload";
import { useWire } from "~/wire";

// Every page body is lazy. Importing any of them eagerly puts its whole
// subtree — the prompt bar's TipTap editor, the chat block renderers — into the
// layout chunk, which has to land before the app can paint at all.
const ChatView = lazy(() => import("./main-content/ChatView"));
const ProjectHomepageView = lazy(() =>
  import("./main-content/ProjectHomepageView").then((m) => ({ default: m.ProjectHomepageView }))
);
const AssetsPage = lazy(() =>
  import("~/components/knowledge/pages/AssetsPage").then((m) => ({ default: m.AssetsPage }))
);
const ChatsPage = lazy(() => import("~/components/knowledge/pages/ChatsPage").then((m) => ({ default: m.ChatsPage })));
const CollectionsPage = lazy(() =>
  import("~/components/knowledge/pages/CollectionsPage").then((m) => ({ default: m.CollectionsPage }))
);
const ConceptsPage = lazy(() =>
  import("~/components/knowledge/pages/ConceptsPage").then((m) => ({ default: m.ConceptsPage }))
);
const LabelsPage = lazy(() =>
  import("~/components/knowledge/pages/LabelsPage").then((m) => ({ default: m.LabelsPage }))
);
const ActionsPage = lazy(() =>
  import("~/components/knowledge/pages/ActionsPage").then((m) => ({ default: m.ActionsPage }))
);
const MemoriesPage = lazy(() =>
  import("~/components/knowledge/pages/MemoriesPage").then((m) => ({ default: m.MemoriesPage }))
);
const SkillsPage = lazy(() =>
  import("~/components/knowledge/pages/SkillsPage").then((m) => ({ default: m.SkillsPage }))
);
const NewCollectionPage = lazy(() =>
  import("~/components/knowledge/pages/NewCollectionPage").then((m) => ({ default: m.NewCollectionPage }))
);
const AssetDetailPage = lazy(() =>
  import("~/components/knowledge/pages/AssetDetailPage").then((m) => ({ default: m.AssetDetailPage }))
);
const ConceptDetailPage = lazy(() =>
  import("~/components/knowledge/pages/ConceptDetailPage").then((m) => ({ default: m.ConceptDetailPage }))
);
const LabelDetailPage = lazy(() =>
  import("~/components/knowledge/pages/LabelDetailPage").then((m) => ({ default: m.LabelDetailPage }))
);
const CollectionDetailPage = lazy(() =>
  import("~/components/knowledge/pages/CollectionDetailPage").then((m) => ({ default: m.CollectionDetailPage }))
);
const ArtifactDetailPage = lazy(() =>
  import("~/components/knowledge/pages/ArtifactDetailPage").then((m) => ({ default: m.ArtifactDetailPage }))
);
const SearchPage = lazy(() =>
  import("~/components/knowledge/pages/SearchPage").then((m) => ({ default: m.SearchPage }))
);
const ScopePickerPage = lazy(() =>
  import("~/components/knowledge/pages/ScopePickerPage").then((m) => ({ default: m.ScopePickerPage }))
);
const RecordPage = lazy(() => import("~/page/input/InputPage").then((m) => ({ default: m.InputPage })));
const CommandCenterSidePanelPage = lazy(() =>
  import("~/components/prompt/internal/components/CommandCenterSidePanelPage").then((m) => ({
    default: m.CommandCenterSidePanelPage,
  }))
);
const LiveSessionsPage = lazy(() =>
  import("~/components/knowledge/pages/LiveSessionsPage").then((m) => ({ default: m.LiveSessionsPage }))
);
const LiveSessionDetailPage = lazy(() =>
  import("~/components/knowledge/pages/LiveSessionDetailPage").then((m) => ({ default: m.LiveSessionDetailPage }))
);
const LiveSessionTagsPage = lazy(() =>
  import("~/components/knowledge/tags-page").then((m) => ({ default: m.LiveSessionTagsPage }))
);
const SkillDetailPage = lazy(() =>
  import("~/components/knowledge/pages/SkillDetailPage").then((m) => ({ default: m.SkillDetailPage }))
);
const ActionDetailPage = lazy(() =>
  import("~/components/knowledge/pages/ActionDetailPage").then((m) => ({ default: m.ActionDetailPage }))
);
const AgentDetailPage = lazy(() =>
  import("~/components/knowledge/pages/AgentDetailPage").then((m) => ({ default: m.AgentDetailPage }))
);

interface PageRendererProps {
  /** The entity ID or page slug to render (e.g., "chat_xxx", "assets", "asset_xxx") */
  id: string | undefined;
}

/**
 * PageRenderer component that renders the appropriate view based on the provided ID.
 * This is a reusable component that can render content for both main and side panels.
 *
 * @param id - The entity ID (e.g., "chat_xxx", "asset_xxx") or page slug (e.g., "assets", "concepts")
 */
export const PageRenderer = (props: PageRendererProps) => {
  const wire = useWire();
  const location = useLocation();

  /** Determines if current page is a page slug (assets, concepts, etc.) vs an entity */
  const isPageSlugRoute = createMemo(() => {
    const id = props.id;
    return id ? isPageSlug(id) : false;
  });

  /** Gets the entity type from the id (chat, asset, concept, etc.) */
  const entityType = createMemo(() => {
    const id = props.id;
    if (!id || isPageSlugRoute()) return undefined;
    return getIdPrefix(id);
  });

  /** Check if we're on the homepage (no id) or "new" (new chat in side panel) */
  const isHomepage = () => !props.id;
  const isNewChat = () => props.id === "new";

  // Derive "project homepage" from the URL, not from async-resolved context.
  // The projectId is in the path and known synchronously; gating this on
  // userContext/projects resolving made it flip from false to true after load, which
  // flashed the marketing homepage before swapping to the project homepage on
  // every open. Keep showing the project homepage while auth is still loading;
  // only fall back to the marketing homepage once auth confirms a guest. An
  // unreachable project is handled separately by the layout's no-access view.
  const isProjectHomepage = createMemo(() => {
    if (!location.pathname.startsWith("/project/")) return false;
    return !(wire.auth?.isReady?.() && wire.auth?.isGuest?.());
  });

  // The URL's project is known synchronously, but its scope loads a beat later.
  // Hold the project homepage on a spinner until then so we don't paint a
  // half-populated page. Gate on activeContext, not projects.get: the
  // ProjectsSync list can stay falsy indefinitely on a session that opens at
  // root and redirects into a project, and would pin the spinner forever,
  // whereas /context resolves for the current URL scope and retries on
  // auth/connectivity recovery.
  const isUrlProjectContextLoaded = createMemo(() => wire.activeContext.isLoadedForCurrentScope());

  /** Live session detail URL: /project/:projectId/live-sessions/:sessionId
   * Chief session IDs are not prefixed (free-form xid), so we can't dispatch
   * via getIdPrefix — match by URL pattern instead. Gated on the "live-sessions"
   * page slug so a side-panel renderer (e.g. id "new") on this same URL doesn't
   * also match the pathname and mirror the detail page. */
  const liveSessionDetailId = createMemo(() => {
    if (props.id !== "live-sessions") return undefined;
    const m = location.pathname.match(/^\/project\/[^/]+\/live-sessions\/([^/?]+)/);
    return m?.[1];
  });
  // The Tags page shares the session route, so it matches ahead of the detail page.
  const isLiveSessionTags = () =>
    props.id === "live-sessions" && /^\/project\/[^/]+\/live-sessions\/tags\/?$/.test(location.pathname);

  return (
    <Switch fallback={<PlaceholderPage title="Unknown Page" description={`Unknown route: ${props.id}`} />}>
      <Match when={isLiveSessionTags()}>
        <LiveSessionTagsPage />
      </Match>
      {/* Live session detail */}
      <Match when={liveSessionDetailId()}>{(id) => <LiveSessionDetailPage sessionId={id()} />}</Match>

      {/* The two homepage arms get their own Suspense. On first open via a client
          redirect, isProjectHomepage flips false->true once project context
          resolves, swapping these arms; the incoming arm suspends briefly. Under
          a single shared boundary that re-suspend deadlocks during the navigation
          transition and blanks the panel until a refresh. Per-arm boundaries keep
          each suspend local so the swap can't pin the parent. */}

      {/* Project homepage - project detail page */}
      <Match when={isHomepage() && isProjectHomepage()}>
        <Suspense fallback={<WorkspaceLoading />}>
          <Show when={isUrlProjectContextLoaded()} fallback={<WorkspaceLoading />}>
            <ProjectHomepageView />
          </Show>
        </Suspense>
      </Match>

      {/* Homepage - Chat input page */}
      <Match when={isHomepage()}>
        <Suspense fallback={<WorkspaceLoading />}>
          <ChatView chatId={undefined} />
        </Suspense>
      </Match>

      {/* New chat - Used for opening a new chat from "Chat with" actions */}
      <Match when={isNewChat()}>
        <ChatView chatId={"new" as StreamingChatId} />
      </Match>

      {/* Page slug routes */}
      <Match when={isPageSlugRoute() && props.id}>
        <PageSlugView slug={props.id as PageSlug} />
      </Match>

      {/* Chat entity */}
      <Match when={entityType() === "chat" && props.id}>
        <ChatView chatId={props.id as StreamingChatId} />
      </Match>

      {/* Asset entity */}
      <Match when={entityType() === "asset" && props.id}>
        <AssetDetailPage assetId={props.id as AssetId} />
      </Match>

      {/* Concept entity */}
      <Match when={entityType() === "concept" && props.id}>
        <ConceptDetailPage conceptId={props.id as ConceptId} />
      </Match>

      {/* Label entity */}
      <Match when={entityType() === "label" && props.id}>
        <LabelDetailPage labelId={props.id as LabelId} />
      </Match>

      {/* View entity */}
      <Match when={entityType() === "view" && props.id}>
        <CollectionDetailPage viewId={props.id as ViewId} />
      </Match>

      {/* Block entity (artifact) */}
      <Match when={entityType() === "block" && props.id}>
        <ArtifactDetailPage blockId={props.id as StreamingBlockId} editMode={wire.navigation.state().edit} />
      </Match>

      {/* Skill entity */}
      <Match when={entityType() === "skill" && props.id}>{(id) => <SkillDetailPage skillId={id()} />}</Match>

      {/* Action entity */}
      <Match when={entityType() === "action" && props.id}>{(id) => <ActionDetailPage actionId={id()} />}</Match>

      {/* Built-in extract agent (Explorer, Librarian, Researcher, …) */}
      <Match when={entityType() === "agent" && props.id}>{(id) => <AgentDetailPage agentEntityId={id()} />}</Match>
    </Switch>
  );
};

/** Renders page slug routes (assets list, concepts list, etc.) */
const PageSlugView = (props: { slug: PageSlug }) => {
  return (
    <Switch>
      <Match when={props.slug === "assets"}>
        <AssetsPage />
      </Match>
      <Match when={props.slug === "concepts"}>
        <ConceptsPage />
      </Match>
      <Match when={props.slug === "labels"}>
        <LabelsPage />
      </Match>
      <Match when={props.slug === "collections"}>
        <CollectionsPage />
      </Match>
      <Match when={props.slug === "skills"}>
        <SkillsPage category="skill" />
      </Match>
      <Match when={props.slug === "personas"}>
        <SkillsPage category="persona" />
      </Match>
      <Match when={props.slug === "actions"}>
        <ActionsPage />
      </Match>
      <Match when={props.slug === "memories"}>
        <MemoriesPage />
      </Match>
      <Match when={props.slug === "new-collection"}>
        <NewCollectionPage />
      </Match>
      <Match when={props.slug === "chats"}>
        <ChatsPage />
      </Match>
      <Match when={props.slug === "search"}>
        <SearchPage />
      </Match>
      <Match when={props.slug === "scope-picker"}>
        <ScopePickerPage />
      </Match>
      <Match when={props.slug === "record"}>
        <RecordPage />
      </Match>
      <Match when={props.slug === "command-center"}>
        <CommandCenterSidePanelPage />
      </Match>
      <Match when={props.slug === "live-sessions"}>
        <LiveSessionsPage />
      </Match>
    </Switch>
  );
};

/** Generic placeholder page component */
const PlaceholderPage = (props: { title: string; description: string }) => {
  return (
    <div class="flex h-full flex-col items-center justify-center p-8">
      <div class="max-w-md text-center">
        <h1 class="mb-2 text-2xl font-semibold text-primary-text">{props.title}</h1>
        <p class="text-secondary-text">{props.description}</p>
        <p class="mt-4 text-xs text-muted-text">This page is under construction</p>
      </div>
    </div>
  );
};
